import React, { useEffect, useState } from 'react'

import {
    Box,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'

import {
    ABBLabel,
    FELabel,
    HasWarrantLabel,
    IPOLabel,
    KVLabel,
    NonCashIssueLabel,
    RELabel,
} from '../../components/entities/IssueTypeLabels'
import InfoBlock from '../components/InfoBlock'
import { InvestorDetails } from '../../api/InvestorApi'
import { hasSourceOfType } from '../../components/utils/calculateIssueStatus'

type YearlyCommitmentSummary = {
    nrOfSubscriptionCommitments: number
    nrOfGarantCommitments: number
    nrOfInvestorCommitments: number
    total: number
    year: string
}

const getIssueYear = (issue: { date: string }) => {
    const issueDate = new Date(issue.date)
    return issueDate.getUTCFullYear()
}

const getNrOfCommitmentsPerType = ({ lowestFeeLevel }: { lowestFeeLevel: number | null }) => {
    return {
        subscriptionCommitment: lowestFeeLevel === 0 ? 1 : 0,
        garantCommitment: lowestFeeLevel !== null && lowestFeeLevel > 0 ? 1 : 0,
        investorCommitment: lowestFeeLevel === null ? 1 : 0,
    }
}

const TableBodyRow = ({
    yearlyCommitmentSummary,
    isMobileScreen,
}: {
    yearlyCommitmentSummary: YearlyCommitmentSummary
    isMobileScreen: boolean
}) => {
    const [isOpen, setIsOpen] = useState(false)

    useEffect(() => {
        if (!isMobileScreen) {
            setIsOpen(false)
        }
    }, [isMobileScreen])

    return (
        <>
            <TableRow>
                <TableCell>{yearlyCommitmentSummary.year}</TableCell>
                {!isMobileScreen ? (
                    <>
                        <TableCell>{yearlyCommitmentSummary.nrOfGarantCommitments}</TableCell>
                        <TableCell>{yearlyCommitmentSummary.nrOfSubscriptionCommitments}</TableCell>
                        <TableCell>{yearlyCommitmentSummary.nrOfInvestorCommitments}</TableCell>
                    </>
                ) : null}
                <TableCell align={isMobileScreen ? 'right' : 'left'}>
                    <b>{yearlyCommitmentSummary.total}</b>
                </TableCell>
                {isMobileScreen ? (
                    <TableCell align='right'>
                        <IconButton aria-label='expand row' size='small' onClick={() => setIsOpen(!isOpen)}>
                            {isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                    </TableCell>
                ) : null}
            </TableRow>
            {isOpen && isMobileScreen ? (
                <TableRow>
                    <TableCell colSpan={3}>
                        <Box
                            sx={{
                                pb: 2,
                            }}
                        >
                            <Table size='small'>
                                <TableHead>
                                    <TableRow>
                                        <TableCell align='center'>
                                            <Typography
                                                variant='body2'
                                                sx={{
                                                    fontWeight: 600,
                                                }}
                                            >
                                                Garantiåtagare
                                            </Typography>
                                        </TableCell>
                                        <TableCell align='center'>
                                            <Typography
                                                variant='body2'
                                                sx={{
                                                    fontWeight: 600,
                                                }}
                                            >
                                                Teckningsåtagare
                                            </Typography>
                                        </TableCell>
                                        <TableCell align='center'>
                                            <Typography
                                                variant='body2'
                                                sx={{
                                                    fontWeight: 600,
                                                }}
                                            >
                                                Deltagare
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    <TableRow>
                                        <TableCell align='center' sx={{ borderBottom: 'none' }}>
                                            {yearlyCommitmentSummary.nrOfGarantCommitments}
                                        </TableCell>
                                        <TableCell align='center' sx={{ borderBottom: 'none' }}>
                                            {yearlyCommitmentSummary.nrOfSubscriptionCommitments}
                                        </TableCell>
                                        <TableCell align='center' sx={{ borderBottom: 'none' }}>
                                            {yearlyCommitmentSummary.nrOfInvestorCommitments}
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </Box>
                    </TableCell>
                </TableRow>
            ) : null}
        </>
    )
}

function InvestorYearlyOverview({ investor }: { investor: InvestorDetails }) {
    const theme = useTheme()
    const isMobileScreen = useMediaQuery(theme.breakpoints.down('sm'))
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'))

    if (!investor?.participations) return null

    // filter out IPO participations where investor is seller
    const participations = investor.participations?.filter((participation) => !participation.commitment.isSeller)

    const uniqueCommitments = participations.reduce(
        (acc, curr) => {
            const { issue, commitment, warrant, convertible } = curr

            if ((issue && hasSourceOfType(issue, 'Cancellation')) || warrant?.cancelled) {
                return acc
            }

            const existingIssueIndex = acc.findIndex((existingIssue) => existingIssue.id === issue?.id)

            if (existingIssueIndex > 0) {
                const existingIssue = acc[existingIssueIndex]

                if (existingIssue.lowestFeeLevel > 0 && commitment.feeLevel > 0) {
                    // If existing issue object and current issue both are garant commitments, then combine objects
                    acc[existingIssueIndex] = {
                        ...existingIssue,
                        lowestFeeLevel:
                            existingIssue.lowestFeeLevel > commitment.feeLevel
                                ? commitment.feeLevel
                                : existingIssue.lowestFeeLevel,
                    }
                    return acc
                }
            }

            acc.push({
                ...issue,
                ...warrant,
                ...convertible,
                date:
                    issue?.subEndDate ||
                    issue?.publicationDate ||
                    warrant?.subEndDate ||
                    convertible?.publicationDate ||
                    null,
                lowestFeeLevel: commitment.feeLevel,
            })

            return acc
        },
        [] as Array<{ date: string | null; lowestFeeLevel: number | null }>,
    )

    // Summarizes all participations to an array with one object per year
    const yearlyCommitmentSummaries = uniqueCommitments
        .reduce(
            (acc, participation) => {
                const issueYear = getIssueYear(participation)
                const nrOfCommitmentsPerType = getNrOfCommitmentsPerType(participation)

                const existingYearlySummaryIndex = acc.findIndex((yearlySummary) => {
                    return yearlySummary.year === issueYear
                })

                if (existingYearlySummaryIndex === -1) {
                    // Object for the year doesn't exist
                    // Only include years from 2021 and forward, until we've built more data for earlier years
                    if (issueYear >= 2021) {
                        acc.push({
                            year: issueYear,
                            nrOfSubscriptionCommitments: nrOfCommitmentsPerType.subscriptionCommitment,
                            nrOfGarantCommitments: nrOfCommitmentsPerType.garantCommitment,
                            nrOfInvestorCommitments: nrOfCommitmentsPerType.investorCommitment,
                            total: 1,
                        })
                    }
                } else {
                    // Object for the year already exists, add values to existing object
                    const existingYearlySummaryObject = acc[existingYearlySummaryIndex]

                    acc[existingYearlySummaryIndex] = {
                        ...existingYearlySummaryObject,
                        nrOfSubscriptionCommitments:
                            existingYearlySummaryObject.nrOfSubscriptionCommitments +
                            nrOfCommitmentsPerType.subscriptionCommitment,
                        nrOfGarantCommitments:
                            existingYearlySummaryObject.nrOfGarantCommitments + nrOfCommitmentsPerType.garantCommitment,
                        nrOfInvestorCommitments:
                            existingYearlySummaryObject.nrOfInvestorCommitments +
                            nrOfCommitmentsPerType.investorCommitment,
                        total: existingYearlySummaryObject.total + 1,
                    }
                }

                return acc
            },
            [] as Array<{
                nrOfSubscriptionCommitments: number
                nrOfGarantCommitments: number
                nrOfInvestorCommitments: number
                total: number
                year: string
            }>,
        )
        .sort((a, b) => a.year - b.year)

    const summaryOfAllCommitments = yearlyCommitmentSummaries.reduce(
        (acc, yearlyCommitmentSummary) => {
            const existingSummaryOfCommitments = acc
            acc = {
                ...existingSummaryOfCommitments,
                nrOfGarantCommitments:
                    existingSummaryOfCommitments.nrOfGarantCommitments + yearlyCommitmentSummary.nrOfGarantCommitments,
                nrOfSubscriptionCommitments:
                    existingSummaryOfCommitments.nrOfSubscriptionCommitments +
                    yearlyCommitmentSummary.nrOfSubscriptionCommitments,
                nrOfInvestorCommitments:
                    existingSummaryOfCommitments.nrOfInvestorCommitments +
                    yearlyCommitmentSummary.nrOfInvestorCommitments,
                total: existingSummaryOfCommitments.total + yearlyCommitmentSummary.total,
            }

            return acc
        },
        {
            label: 'Totalt',
            nrOfGarantCommitments: 0,
            nrOfSubscriptionCommitments: 0,
            nrOfInvestorCommitments: 0,
            total: 0,
        },
    )

    return (
        <InfoBlock title='Sammanställning'>
            <TableContainer>
                <Table size='small'>
                    <TableHead>
                        <TableRow>
                            <TableCell />
                            {!isMobileScreen ? (
                                <>
                                    <TableCell>
                                        <Stack
                                            direction='row'
                                            spacing={2}
                                            component='span'
                                            sx={{
                                                alignItems: 'center',
                                            }}
                                        >
                                            <Typography variant='body2'>Garantiåtagare</Typography>
                                            {!isSmallScreen ? (
                                                <>
                                                    <FELabel />
                                                    <IPOLabel />
                                                    <HasWarrantLabel />
                                                </>
                                            ) : null}
                                        </Stack>
                                    </TableCell>
                                    <TableCell>
                                        <Stack
                                            direction='row'
                                            spacing={2}
                                            component='span'
                                            sx={{
                                                alignItems: 'center',
                                            }}
                                        >
                                            <Typography variant='body2'>Teckningsåtagare</Typography>
                                            {!isSmallScreen ? (
                                                <>
                                                    <FELabel />
                                                    <IPOLabel />
                                                </>
                                            ) : null}
                                        </Stack>
                                    </TableCell>
                                    <TableCell>
                                        <Stack
                                            direction='row'
                                            spacing={2}
                                            component='span'
                                            sx={{
                                                alignItems: 'center',
                                            }}
                                        >
                                            <Typography variant='body2'>Deltagare</Typography>
                                            {!isSmallScreen ? (
                                                <>
                                                    <RELabel />
                                                    <ABBLabel />
                                                    <NonCashIssueLabel />
                                                    <KVLabel />
                                                </>
                                            ) : null}
                                        </Stack>
                                    </TableCell>
                                </>
                            ) : null}
                            <TableCell align={isMobileScreen ? 'right' : 'left'}>
                                <b>Totalt</b>
                            </TableCell>
                            {isMobileScreen ? <TableCell width={50} align='right' /> : null}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {yearlyCommitmentSummaries.map((yearlyCommitmentSummary) => {
                            return (
                                <TableBodyRow
                                    key={yearlyCommitmentSummary.year}
                                    yearlyCommitmentSummary={yearlyCommitmentSummary}
                                    isMobileScreen={isMobileScreen}
                                />
                            )
                        })}
                        <TableRow>
                            <TableCell>
                                <b>Totalt</b>
                            </TableCell>
                            {!isMobileScreen ? (
                                <>
                                    <TableCell>
                                        <b>{summaryOfAllCommitments.nrOfGarantCommitments}</b>
                                    </TableCell>
                                    <TableCell>
                                        <b>{summaryOfAllCommitments.nrOfSubscriptionCommitments}</b>
                                    </TableCell>
                                    <TableCell>
                                        <b>{summaryOfAllCommitments.nrOfInvestorCommitments}</b>
                                    </TableCell>
                                </>
                            ) : null}
                            <TableCell align={isMobileScreen ? 'right' : 'left'}>
                                <b>{summaryOfAllCommitments.total}</b>
                            </TableCell>
                            {isMobileScreen ? <TableCell align='right' /> : null}
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>
        </InfoBlock>
    )
}

export default InvestorYearlyOverview
